import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { ThemeProps } from "@styles/theme";

export type PointDT = { time: Date; value: number | null };
export type ZoneDT = { start: Date; end: Date; label?: string };

export type AxisZone = {
  start: any;
  end: any;
  index: number;
  label?: string;
  idxLabel?: string,
  parentIdx?: number,
  childIdx?: number;
  id?: string;
};

export type ZoneLabelInfo = {
  id?: string;
  idxLabel: string;
  label: string;
  start: any;
  end: any;
};

export type IdxGroupClickInfo = {
  zones: ZoneLabelInfo[];
  x: number;
  y: number;
};

export type ZonesOverlayVerticalProps = {
  xAxisMap?: Record<string, any>;
  offset?: { top: number; bottom: number; left: number; width: number; height: number };

  xAxisId: string | number;

  zones?: AxisZone[];

  fixedIntervalPx?: number;

  bottomLabelYPad?: number;

  onIdxGroupClick?: (group: IdxGroupClickInfo) => void;
  highlightedZoneId?: string | null;

  showFill?: boolean;
  fillOpacity?: number;
  lineOpacity?: number;
  evenFill?: string;
  oddFill?: string;
  stroke?: string;
  strokeWidth? : number;
  fontSize?: number;

  hideLabels?: boolean;
  hideLabel?: boolean;
  showIdx?: boolean;

  includeSmall?: boolean;

  fixedLabelFormatter?: (info: {
    index: number;
    xLeftPx: number;
    xRightPx: number;
    startCategory?: any;
    endCategory?: any;
  }) => React.ReactNode;
};

function getCategoryStepPx(scale: any, domain: any[]): number | null {
    if (typeof scale?.step === "function") {
        const step = Number(scale.step());
        if (Number.isFinite(step) && step > 0) {return step;}
    }
    if (domain.length >= 2) {
        const p0 = Number(scale(domain[0]));
        const p1 = Number(scale(domain[1]));
        const step = Math.abs(p1 - p0);
        if (Number.isFinite(step) && step > 0) {return step;}
    }

    return null;
}

function getBandWidthPx(scale: any): number {
    if (typeof scale?.bandwidth === "function") {
        const bw = Number(scale.bandwidth());
        if (Number.isFinite(bw) && bw >= 0) {return bw;}
    }

    return 0;
}

const LABEL_COLLISION_PAD = 8;

function estimateTextWidthPx(text: string, fontSize: number): number {
    return text.length * fontSize * 0.62;
}

function compareStepLabels(left: string, right: string): number {
    const leftParts = left.split('.');
    const rightParts = right.split('.');
    const length = Math.max(leftParts.length, rightParts.length);

    for (let i = 0; i < length; i++) {
        const l = leftParts[i];
        const r = rightParts[i];
        if (l === undefined) {return -1;}
        if (r === undefined) {return 1;}

        const ln = Number(l);
        const rn = Number(r);
        if (Number.isFinite(ln) && Number.isFinite(rn)) {
            if (ln !== rn) {return ln - rn;}
        } else if (l !== r) {
            return l.localeCompare(r);
        }
    }

    return 0;
}

function formatIdxGroupLabel(idxLabels: string[]): string {
    const distinctLabels = Array.from(new Set(idxLabels)).sort(compareStepLabels);

    if (distinctLabels.length === 1) {
        return distinctLabels[0];
    }

    if (distinctLabels.every(label => /^\d+$/.test(label))) {
        const numbers = distinctLabels.map(Number);
        const isConsecutive = numbers.every((n, i) => i === 0 || n === numbers[i - 1] + 1);
        if (isConsecutive) {
            return `${numbers[0]}–${numbers[numbers.length - 1]}`;
        }
    }

    if (distinctLabels.length <= 2) {
        return distinctLabels.join(' - ');
    }

    return `${distinctLabels.slice(0, 2).join(' - ')} +${distinctLabels.length - 2}`;
}

export const ZonesOverlayVertical: React.FC<ZonesOverlayVerticalProps> = ({
    xAxisMap,
    offset,
    xAxisId,
    zones = [],
    fixedIntervalPx,

    bottomLabelYPad = 28,

    showFill = true,
    fillOpacity = 0.15,
    lineOpacity = 0.35,
    evenFill = "#888",
    oddFill = "#888",
    stroke = "#666",
    strokeWidth = 1,
    fontSize = 16,

    hideLabels = false,
    hideLabel = false,
    showIdx = false,

    includeSmall = false,

    fixedLabelFormatter,

    onIdxGroupClick,
    highlightedZoneId = null,
}) => {
    const theme = useTheme<ThemeProps>();
    const [hoveredIdxGroupIndex, setHoveredIdxGroupIndex] = useState<number | null>(null);
    if (!xAxisMap || !offset) {return null;}

    const axis = xAxisMap[String(xAxisId)];
    const scale = axis?.scale as any;
    if (!scale) {return null;}

    const plotTop = offset.top;
    const plotBottom = offset.top + offset.height;
    const labelY = plotBottom + bottomLabelYPad;

    const domain: any[] = typeof scale.domain === "function" ? scale.domain() : [];
    const isCategoryAxis =
    domain.length > 0 && (typeof domain[0] === "string" || typeof domain[0] === "number");

    if (fixedIntervalPx && fixedIntervalPx > 0) {
        let xMin = offset.left;
        let xMax = offset.left + offset.width;

        if (isCategoryAxis && domain.length > 0) {
            const bw = getBandWidthPx(scale);
            const p0 = Number(scale(domain[0]));
            const pLast = Number(scale(domain[domain.length - 1]));
            if (Number.isFinite(p0) && Number.isFinite(pLast)) {
                xMin = Math.min(p0, pLast);
                xMax = Math.max(p0, pLast) + bw;
            }
        } else if (domain.length >= 2) {
            const p0 = Number(scale(domain[0]));
            const p1 = Number(scale(domain[domain.length - 1]));
            if (Number.isFinite(p0) && Number.isFinite(p1)) {
                xMin = Math.min(p0, p1);
                xMax = Math.max(p0, p1);
            }
        }

        let stepPx = Math.max(1, fixedIntervalPx);
        let categoriesPerBand = 0;

        if (isCategoryAxis && domain.length > 0) {
            const catStep = getCategoryStepPx(scale, domain);
            if (catStep) {
                const k = Math.max(1, Math.round(stepPx / catStep));
                stepPx = k * catStep;
                categoriesPerBand = k;
            }
        }

        const bands: Array<{
      left: number;
      right: number;
      idx: number;
      startCategory?: any;
      endCategory?: any;
    }> = [];

        let idx = 0;
        for (let left = xMin; left < xMax; left += stepPx) {
            const right = Math.min(left + stepPx, xMax);

            let startCategory: any | undefined;
            let endCategory: any | undefined;

            if (isCategoryAxis && categoriesPerBand > 0 && domain.length > 0) {
                const startIndex = idx * categoriesPerBand;
                const endIndex = Math.min(startIndex + categoriesPerBand - 1, domain.length - 1);
                startCategory = domain[startIndex];
                endCategory = domain[endIndex];
            }

            bands.push({ left, right, idx, startCategory, endCategory });
            idx++;
        }

        return (
            <g>
                {bands.map((b, i) => {
                    const w = Math.max(0, b.right - b.left);
                    if (w <= 0) {return null;}
                    const mid = b.left + w / 2;

                    const label =
            fixedLabelFormatter?.({
                index: b.idx,
                xLeftPx: b.left,
                xRightPx: b.right,
                startCategory: b.startCategory,
                endCategory: b.endCategory,
            }) ??
            (b.startCategory !== undefined
                ? `${b.startCategory}${b.endCategory !== b.startCategory ? `–${b.endCategory}` : ""}`
                : String(b.idx + 1));

                    return (
                        <g key={`fx-${b.idx}`}>
                            {showFill && (
                                <rect
                                    x={b.left}
                                    y={plotTop}
                                    width={w}
                                    height={offset.height}
                                    opacity={fillOpacity}
                                    fill={i % 2 == 0 ? evenFill : oddFill}
                                />
                            )}

                            {/* <line
                x1={b.left}
                x2={b.left}
                y1={plotTop}
                y2={plotBottom}
                stroke={stroke}
                strokeOpacity={lineOpacity}
              /> */}
                            <line
                                x1={b.right}
                                x2={b.right}
                                y1={plotTop}
                                y2={plotBottom}
                                stroke={stroke}
                                strokeOpacity={lineOpacity}
                            />
                            {!hideLabels &&
                <>
                    {showIdx &&
                    <text x={mid} y={labelY} textAnchor="middle" fontSize={fontSize} fill={theme.colors.black}>
                        {b.idx + 1}
                    </text>
                    }
                    {!hideLabel &&
                    <text x={mid} y={showIdx ? labelY + 50 : labelY} textAnchor="middle" fontSize={fontSize} fill={theme.colors.black}>
                        {label}
                    </text>
                    }
                </>
                            }
                        </g>
                    );
                })}
            </g>
        );
    }

    return (
        <g>
            {(() => {
                const renderedZones = zones.map((z, i) => {
                    const x1 = Number(scale(z.start));
                    const x2 = Number(scale(z.end));
                    const left = Math.min(x1, x2);
                    const right = Math.max(x1, x2);
                    const w = Math.max(0, right - left);
                    if (includeSmall ? w <= 0 : Number(w.toFixed(0)) <= 1) {return null;}

                    const previous = i != 0 ? zones[i-1] : null;
                    const mid = left + w / 2;

                    return { z, i, left, right, w, previous, mid };
                }).filter(Boolean) as Array<{ z: AxisZone; i: number; left: number; right: number; w: number; previous: AxisZone | null; mid: number }>;

                const labelItems = renderedZones
                    .map(zoneInfo => {
                        const idxLabel = String(zoneInfo.z.idxLabel ?? zoneInfo.i + 1);

                        return {
                            left: zoneInfo.left,
                            right: zoneInfo.right,
                            mid: zoneInfo.mid,
                            idxLabels: [idxLabel],
                            labels: zoneInfo.z.label ? [zoneInfo.z.label] : [],
                            midSum: zoneInfo.mid,
                            zoneInfos: [{
                                id: zoneInfo.z.id,
                                idxLabel,
                                label: zoneInfo.z.label ?? '',
                                start: zoneInfo.z.start,
                                end: zoneInfo.z.end,
                            } as ZoneLabelInfo],
                        };
                    })
                    .sort((a, b) => a.mid - b.mid);

                // Merging shifts a group's anchor (mean of member mids), which can create new
                // collisions with already-accepted neighbours — iterate until stable.
                let collidedGroups = labelItems;
                let mergedSomething = true;
                while (mergedSomething) {
                    mergedSomething = false;
                    collidedGroups = collidedGroups.reduce<typeof labelItems>((acc, group) => {
                        const previousGroup = acc[acc.length - 1];

                        if (previousGroup) {
                            const previousHalf = estimateTextWidthPx(formatIdxGroupLabel(previousGroup.idxLabels), fontSize) / 2;
                            const currentHalf = estimateTextWidthPx(formatIdxGroupLabel(group.idxLabels), fontSize) / 2;

                            if (group.mid - currentHalf - LABEL_COLLISION_PAD < previousGroup.mid + previousHalf) {
                                previousGroup.left = Math.min(previousGroup.left, group.left);
                                previousGroup.right = Math.max(previousGroup.right, group.right);
                                previousGroup.idxLabels.push(...group.idxLabels);
                                previousGroup.labels.push(...group.labels);
                                previousGroup.zoneInfos.push(...group.zoneInfos);
                                previousGroup.midSum += group.midSum;
                                previousGroup.mid = previousGroup.midSum / previousGroup.idxLabels.length;
                                mergedSomething = true;

                                return acc;
                            }
                        }

                        acc.push(group);

                        return acc;
                    }, []);
                }

                const formatGroupedLabel = (values: string[]) => Array.from(new Set(values)).join(' - ');

                return (
                    <>
                        {renderedZones.map(({ z, i, left, right, w, previous }) => {

                            return (
                                <g key={`${String(z.start)}-${String(z.end)}-${i}`}>
                                    {(showFill) && (
                                        <rect x={left} y={plotTop} width={w} height={offset.height} opacity={fillOpacity} fill={i % 2 == 0 ? evenFill : oddFill} />
                                    )}
                                    {((previous && previous.end != z.start) || !previous) &&
              <line x1={left} x2={left} y1={plotTop} y2={plotBottom  + offset.bottom} stroke={stroke} strokeOpacity={lineOpacity} strokeWidth={strokeWidth}/>
                                    }
                                    <line x1={right} x2={right} y1={plotTop} y2={plotBottom  + offset.bottom} stroke={stroke} strokeOpacity={lineOpacity} strokeWidth={strokeWidth}/>
                                </g>
                            );
                        })}
                        {highlightedZoneId && renderedZones
                            .filter(({ z }) => z.id && z.id === highlightedZoneId)
                            .map(({ z, left, w }) => (
                                <rect
                                    key={`zone-highlight-${z.id}`}
                                    x={left}
                                    y={plotTop}
                                    width={w}
                                    height={offset.height}
                                    fill={theme.colors.violet_primary}
                                    opacity={0.18}
                                    pointerEvents="none"
                                />
                            ))}
                        {!hideLabels && (
                            <>
                                {showIdx && collidedGroups.map((group, index) => {
                                    const groupText = formatIdxGroupLabel(group.idxLabels);
                                    const isInteractive = Boolean(onIdxGroupClick && group.zoneInfos.length > 1);

                                    if (!isInteractive) {
                                        return (
                                            <text key={`group-idx-${index}`} x={group.mid} y={labelY} textAnchor="middle" fontSize={fontSize} fill={theme.colors.black}>
                                                {groupText}
                                            </text>
                                        );
                                    }

                                    const pillWidth = estimateTextWidthPx(groupText, fontSize) + 4;
                                    const pillHeight = fontSize + 10;
                                    const pillTop = labelY - fontSize - 2;
                                    const hovered = hoveredIdxGroupIndex === index;

                                    return (
                                        <g
                                            key={`group-idx-${index}`}
                                            data-idx-group={groupText}
                                            cursor="pointer"
                                            onMouseEnter={() => setHoveredIdxGroupIndex(index)}
                                            onMouseLeave={() => setHoveredIdxGroupIndex(current => current === index ? null : current)}
                                            onClick={() => onIdxGroupClick!({
                                                zones: [...group.zoneInfos].sort((a, b) => compareStepLabels(a.idxLabel, b.idxLabel)),
                                                x: group.mid,
                                                y: pillTop,
                                            })}
                                        >
                                            <rect
                                                x={group.mid - pillWidth / 2}
                                                y={pillTop}
                                                width={pillWidth}
                                                height={pillHeight}
                                                rx={pillHeight / 2}
                                                ry={pillHeight / 2}
                                                fill={hovered ? theme.colors.transp_violet : theme.colors.icon_background}
                                                stroke={theme.colors.violet_primary}
                                                strokeOpacity={hovered ? 0.9 : 0.45}
                                                strokeWidth={1}
                                            />
                                            <text x={group.mid} y={labelY} textAnchor="middle" fontSize={fontSize} fill={theme.colors.black}>
                                                {groupText}
                                            </text>
                                        </g>
                                    );
                                })}
                                {!hideLabel && collidedGroups.map((group, index) => (
                                    <text key={`group-label-${index}`} x={group.mid} y={showIdx ? labelY + 50 : labelY} textAnchor="middle" fontSize={fontSize} fill={theme.colors.black}>
                                        {formatGroupedLabel(group.labels)}
                                    </text>
                                ))}
                            </>
                        )}
                    </>
                );
            })()}
        </g>
    );
};

export type ZonesOverlayHorizontalProps = {
  yAxisMap?: Record<string, any>;
  offset?: { top: number; left: number; width: number; height: number };

  yAxisId: string | number;

  zones?: AxisZone[];

  fixedIntervalPx?: number;

  sideLabelXPad?: number;
  labelSide?: "left" | "right";
  hideLabels?: boolean;
  hideLabel?: boolean;
  showIdx?: boolean;

  labelLegend?: string;

  showFill?: boolean;
  fillOpacity?: number;
  lineOpacity?: number;
  evenFill?: string;
  oddFill?: string;

  hideStrokes?: boolean;
  stroke?: string;
  strokeWidth?: number;
  fontSize?: number;

  onLabelClick?: (id: string) => {};

  fixedLabelFormatter?: (info: {
    index: number;
    yTopPx: number;
    yBottomPx: number;
    startCategory?: any;
    endCategory?: any;
  }) => React.ReactNode;

  maxLabelWidth?: number;
};

export const ZonesOverlayHorizontal: React.FC<ZonesOverlayHorizontalProps> = ({
    yAxisMap,
    offset,
    yAxisId,
    zones = [],
    // fixedIntervalPx,

    sideLabelXPad = 28,
    labelSide = "left",
    hideLabels = false,
    hideLabel = false,
    showIdx = false,
    labelLegend = '',

    showFill = false,
    fillOpacity = 0.08,
    lineOpacity = 0.35,
    evenFill = "#888",
    oddFill = "#888",
    hideStrokes = false,
    stroke = "#666",
    strokeWidth = 1,
    fontSize = 16,

    onLabelClick,

    fixedLabelFormatter,

    maxLabelWidth = 0,
}) => {
    if (!yAxisMap || !offset) {return null;}

    const theme = useTheme<ThemeProps>();
    const [hoveredZoneKey, setHoveredZoneKey] = useState<string | null>(null);

    const renderWrappedLabel = (text: string, x: number, y: number, width: number, lineHeight: number) => (
        <foreignObject
            x={x}
            y={y - lineHeight * 1.5}
            width={Math.max(80, width)}
            height={lineHeight * 3}
            pointerEvents="none"
            requiredExtensions="http://www.w3.org/1999/xhtml"
        >
            <div
                style={{
                    height: `${lineHeight * 3}px`,
                    fontSize,
                    lineHeight: `${lineHeight}px`,
                    color: theme.colors.black,
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {text}
                </div>
            </div>
        </foreignObject>
    );

    const axis = yAxisMap[String(yAxisId)];
    const scale = axis?.scale as any;
    if (!scale) {return null;}

    const plotLeft = offset.left;
    const plotRight = offset.left + offset.width;

    const labelX = sideLabelXPad;

    const domain: any[] = typeof scale.domain === "function" ? scale.domain() : [];
    const isCategoryAxis = domain.length > 0 && (typeof domain[0] === "string" || typeof domain[0] === "number");

    if (isCategoryAxis && domain.length > 0) {
        const bw = getBandWidthPx(scale) || 0;
        const plotTop = offset.top;
        const plotBottom = offset.top + offset.height;

        const bands = domain.map((cat, idx) => {
            const p = Number(scale(cat));
            if (!Number.isFinite(p)) {return null;}

            const top = p;
            const bottom = p + bw;

            // clip to plotting area
            const cTop = Math.max(plotTop, Math.min(top, bottom));
            const cBottom = Math.min(plotBottom, Math.max(top, bottom));
            if (cBottom <= cTop) {return null;}

            return {
                idx,
                category: cat,
                top: cTop,
                bottom: cBottom,
            };
        }).filter(Boolean) as Array<{ idx: number; category: any; top: number; bottom: number }>;


        const legendH = bands[0].top;
        const legendMid = 0 + legendH / 2;

        return (
            <g>
                {(!hideLabels && labelLegend) &&
          <g>
              {labelLegend && showIdx &&
              <line
                  x1={labelX + 40}
                  x2={labelX + 40}
                  y1={bands[0].top}
                  y2={0}
                  stroke={stroke}
                  strokeOpacity={lineOpacity}
                  strokeWidth={strokeWidth}
              />

              }

              {labelLegend && !hideLabel &&
              <text
                  x={showIdx ? labelX + 50 : labelX}
                  y={legendMid}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fontSize={fontSize}
                  fill={theme.colors.black}
              >
                  {labelLegend}
              </text>
              }
              <line
                  x1={0}
                  x2={plotRight}
                  y1={bands[0].top}
                  y2={bands[0].top}
                  stroke={stroke}
                  strokeOpacity={lineOpacity}
                  strokeWidth={strokeWidth}
              />
          </g>
                }
                {bands.map((b, i) => {
                    const h = b.bottom - b.top;
                    const mid = b.top + h / 2;

                    // optional: map to your zones label by index, fallback to category
                    const z = zones[i];
                    const label =
            fixedLabelFormatter?.({
                index: b.idx,
                yTopPx: b.top,
                yBottomPx: b.bottom,
                startCategory: b.category,
                endCategory: b.category,
            }) ??
            z?.label ??
            String(b.category);

                    const indentLevel = z?.parentIdx != null ? 1 : 0;
                    const labelOffset = indentLevel * 16;

                    const zoneKey = `cat-band-${String(b.category)}-${b.idx}`;
                    const hovered = hoveredZoneKey === zoneKey;

                    return (
                        <g key={zoneKey}
                            onMouseEnter={() => setHoveredZoneKey(zoneKey)}
                            onMouseLeave={() => setHoveredZoneKey(current => current === zoneKey ? null : current)}
                            onClick={() => onLabelClick ? onLabelClick(z.id!) : {}}>
                            {showFill && (
                                <rect
                                    x={plotLeft}
                                    y={b.top}
                                    width={offset.width}
                                    height={h}
                                    opacity={fillOpacity}
                                    fill={i % 2 === 0 ? evenFill : oddFill}
                                />
                            )}
                            {!hideStrokes &&
                <line
                    x1={0}
                    x2={plotRight}
                    y1={b.bottom}
                    y2={b.bottom}
                    stroke={stroke}
                    strokeOpacity={lineOpacity}
                    strokeWidth={strokeWidth}
                />
                            }

                            {!hideLabels &&
                <>
                    <rect
                        x={0}
                        y={mid - h/2}
                        width={maxLabelWidth + (showIdx ? 100 : 0) + labelOffset}
                        height={h}
                        fill={hovered ? theme.colors.surface_elevated : "transparent"}
                    />
                    {showIdx &&
                    <>
                        <text
                            x={labelX}
                            y={mid}
                            textAnchor="start"
                            dominantBaseline="middle"
                            fontSize={fontSize}
                            fill={theme.colors.black}
                        >
                            {z?.idxLabel ?? b.idx + 1}
                        </text>

                        <line
                            x1={labelX + 40}
                            x2={labelX + 40}
                            y1={b.bottom}
                            y2={b.top}
                            stroke={stroke}
                            strokeOpacity={lineOpacity}
                            strokeWidth={strokeWidth}
                        />
                    </>
                    }

                    {!hideLabel &&
                    renderWrappedLabel(String(label), (showIdx ? labelX + 50 : labelX) + labelOffset, mid, Math.max(100, maxLabelWidth - labelOffset - 8), Math.round(fontSize * 1.2))
                    }

                    <line
                        x1={0}
                        x2={plotLeft}
                        y1={b.bottom}
                        y2={b.bottom}
                        stroke={stroke}
                        strokeOpacity={lineOpacity}
                        strokeWidth={strokeWidth}
                    />
                </>
                            }
                        </g>
                    );
                })}
            </g>
        );
    }

    return (
        <g>
            {zones.map((z, i) => {
                const y1 = Number(scale(z.start));
                const y2 = Number(scale(z.end));
                const top = Math.min(y1, y2);
                const bottom = Math.max(y1, y2);
                const h = Math.max(0, bottom - top);
                if (h <= 0) {return null;}

                const mid = top + h / 2;

                const zoneKey = `${String(z.start)}-${String(z.end)}-${i}`;

                return (
                    <g key={zoneKey}
                        onMouseEnter={() => setHoveredZoneKey(zoneKey)}
                        onMouseLeave={() => setHoveredZoneKey(current => current === zoneKey ? null : current)}
                        onClick={() => onLabelClick ? onLabelClick(z.id!) : {}}>
                        {showFill && (
                            <rect x={plotLeft} y={top} width={offset.width} height={h} opacity={fillOpacity} fill={i % 2 == 0 ? evenFill : oddFill} />
                        )}

                        {!hideStrokes &&
              <>
                  <line x1={plotLeft} x2={plotRight} y1={top} y2={top} stroke={stroke} strokeOpacity={lineOpacity} />
                  <line x1={plotLeft} x2={plotRight} y1={bottom} y2={bottom} stroke={stroke} strokeOpacity={lineOpacity} />
              </>
                        }

                        {!hideLabels &&
              <>
                  {showIdx &&
                  <text
                      x={labelX}
                      y={mid}
                      textAnchor={labelSide === "left" ? "end" : "start"}
                      dominantBaseline="middle"
                      fontSize={fontSize}
                      fill={theme.colors.black}
                  >
                      {z.index ?? String(i + 1)}
                  </text>
                  }
                  {!hideLabel &&
                  renderWrappedLabel(String(z.label ?? 'no label set'), showIdx ? labelX + 50 : labelX, mid, Math.max(100, maxLabelWidth - 8), Math.round(fontSize * 1.2))
                  }
              </>
                        }
                    </g>
                );
            })}
        </g>
    );
};